import { defineAsyncComponent, type Component } from "vue";

/**
 * Icon Map Configuration
 *
 * Add your icons here to make them available in VIcon component.
 * Format: "collection:name": defineAsyncComponent(() => import("~icons/collection/name"))
 *
 * Find icons at:
 * - Material Design Icons: https://pictogrammers.com/library/mdi/
 * - Heroicons: https://heroicons.com/
 * - Lucide: https://lucide.dev/icons/
 */

export const iconMap: Record<string, Component> = {
  // ==================== Material Design Icons ====================

  // Navigation & UI
  "mdi:home": defineAsyncComponent(() => import("~icons/mdi/home")),
  "mdi:menu": defineAsyncComponent(() => import("~icons/mdi/menu")),
  "mdi:close": defineAsyncComponent(() => import("~icons/mdi/close")),
  "mdi:chevron-left": defineAsyncComponent(() => import("~icons/mdi/chevron-left")),
  "mdi:chevron-right": defineAsyncComponent(() => import("~icons/mdi/chevron-right")),
  "mdi:chevron-up": defineAsyncComponent(() => import("~icons/mdi/chevron-up")),
  "mdi:chevron-down": defineAsyncComponent(() => import("~icons/mdi/chevron-down")),
  "mdi:arrow-left": defineAsyncComponent(() => import("~icons/mdi/arrow-left")),
  "mdi:arrow-right": defineAsyncComponent(() => import("~icons/mdi/arrow-right")),
  "mdi:google": defineAsyncComponent(() => import("~icons/mdi/google")),
  "mdi:email-outline": defineAsyncComponent(() => import("~icons/mdi/email-outline")),
  "mdi:lock-outline": defineAsyncComponent(() => import("~icons/mdi/lock-outline")),
  "mdi:github": defineAsyncComponent(() => import("~icons/mdi/github")),
  // Actions
  "mdi:search": defineAsyncComponent(() => import("~icons/mdi/magnify")),
  "mdi:settings": defineAsyncComponent(() => import("~icons/mdi/cog")),
  "mdi:edit": defineAsyncComponent(() => import("~icons/mdi/pencil")),
  "mdi:delete": defineAsyncComponent(() => import("~icons/mdi/delete")),
  "mdi:plus": defineAsyncComponent(() => import("~icons/mdi/plus")),
  "mdi:minus": defineAsyncComponent(() => import("~icons/mdi/minus")),
  "mdi:check": defineAsyncComponent(() => import("~icons/mdi/check")),
  "mdi:close-thick": defineAsyncComponent(() => import("~icons/mdi/close-thick")),
  "mdi:save": defineAsyncComponent(() => import("~icons/mdi/content-save")),
  "mdi:sort-variant-remove": defineAsyncComponent(() => import("~icons/mdi/sort-variant-remove")),
  "mdi:account-outline": defineAsyncComponent(() => import("~icons/mdi/account-outline")),
  "mdi:lock-check-outline": defineAsyncComponent(() => import("~icons/mdi/lock-check-outline")),

  // Status & Alerts
  "mdi:alert": defineAsyncComponent(() => import("~icons/mdi/alert")),
  "mdi:info": defineAsyncComponent(() => import("~icons/mdi/information")),
  "mdi:information": defineAsyncComponent(() => import("~icons/mdi/information")),
  "mdi:warning": defineAsyncComponent(() => import("~icons/mdi/alert-circle")),
  "mdi:error": defineAsyncComponent(() => import("~icons/mdi/alert-octagon")),
  "mdi:success": defineAsyncComponent(() => import("~icons/mdi/check-circle")),
  "mdi:check-circle": defineAsyncComponent(() => import("~icons/mdi/check-circle")),
  "mdi:loading": defineAsyncComponent(() => import("~icons/mdi/loading")),
  "mdi:account-circle": defineAsyncComponent(() => import("~icons/mdi/account-circle")),
  "mdi:shield-lock": defineAsyncComponent(() => import("~icons/mdi/shield-lock")),
  "mdi:credit-card": defineAsyncComponent(() => import("~icons/mdi/credit-card")),

  // User & Account
  "mdi:user": defineAsyncComponent(() => import("~icons/mdi/account")),
  "mdi:user-circle": defineAsyncComponent(() => import("~icons/mdi/account-circle")),
  "mdi:users": defineAsyncComponent(() => import("~icons/mdi/account-group")),
  "mdi:login": defineAsyncComponent(() => import("~icons/mdi/login")),
  "mdi:logout": defineAsyncComponent(() => import("~icons/mdi/logout")),
  "mdi:account": defineAsyncComponent(() => import("~icons/mdi/account")),
  "mdi:cog": defineAsyncComponent(() => import("~icons/mdi/cog")),
  "mdi:white-balance-sunny": defineAsyncComponent(() => import("~icons/mdi/white-balance-sunny")),
  "mdi:moon-waning-crescent": defineAsyncComponent(() => import("~icons/mdi/moon-waning-crescent")),
  "mdi:star-four-points": defineAsyncComponent(() => import("~icons/mdi/star-four-points")),
  "mdi:rocket-launch": defineAsyncComponent(() => import("~icons/mdi/rocket-launch")),
  // Communication
  "mdi:email": defineAsyncComponent(() => import("~icons/mdi/email")),
  "mdi:message-text": defineAsyncComponent(() => import("~icons/mdi/message-text")),
  "mdi:phone": defineAsyncComponent(() => import("~icons/mdi/phone")),
  "mdi:message": defineAsyncComponent(() => import("~icons/mdi/message")),
  "mdi:bell": defineAsyncComponent(() => import("~icons/mdi/bell")),
  "mdi:mail": defineAsyncComponent(() => import("~icons/mdi/email-outline")),

  // Building & Development
  "mdi:hammer": defineAsyncComponent(() => import("~icons/mdi/hammer")),
  "mdi:cloud": defineAsyncComponent(() => import("~icons/mdi/cloud")),
  "mdi:layers": defineAsyncComponent(() => import("~icons/mdi/layers")),
  "mdi:package": defineAsyncComponent(() => import("~icons/mdi/package-variant")),
  "mdi:database": defineAsyncComponent(() => import("~icons/mdi/database")),
  "mdi:table": defineAsyncComponent(() => import("~icons/mdi/table")),
  "mdi:library": defineAsyncComponent(() => import("~icons/mdi/library-shelves")),

  // Charts & Analytics
  "mdi:chart-line": defineAsyncComponent(() => import("~icons/mdi/chart-line")),
  "mdi:chart-bar": defineAsyncComponent(() => import("~icons/mdi/chart-bar")),
  "mdi:chart-box": defineAsyncComponent(() => import("~icons/mdi/chart-box")),
  "mdi:chart-timeline": defineAsyncComponent(() => import("~icons/mdi/chart-timeline-variant")),
  "mdi:chart-bubble": defineAsyncComponent(() => import("~icons/mdi/chart-bubble")),
  "mdi:view-dashboard": defineAsyncComponent(() => import("~icons/mdi/view-dashboard")),

  // KPI & Finance Icons
  "mdi:currency-usd": defineAsyncComponent(() => import("~icons/mdi/currency-usd")),
  "mdi:cash": defineAsyncComponent(() => import("~icons/mdi/cash")),
  "mdi:trending-up": defineAsyncComponent(() => import("~icons/mdi/trending-up")),
  "mdi:trending-down": defineAsyncComponent(() => import("~icons/mdi/trending-down")),
  "mdi:arrow-up": defineAsyncComponent(() => import("~icons/mdi/arrow-up")),
  "mdi:arrow-down": defineAsyncComponent(() => import("~icons/mdi/arrow-down")),
  "mdi:cart": defineAsyncComponent(() => import("~icons/mdi/cart")),
  "mdi:shopping": defineAsyncComponent(() => import("~icons/mdi/shopping")),
  "mdi:percent": defineAsyncComponent(() => import("~icons/mdi/percent")),
  "mdi:finance": defineAsyncComponent(() => import("~icons/mdi/finance")),
  "mdi:chart-areaspline": defineAsyncComponent(() => import("~icons/mdi/chart-areaspline")),
  "mdi:thumb-up": defineAsyncComponent(() => import("~icons/mdi/thumb-up")),
  "mdi:thumb-down": defineAsyncComponent(() => import("~icons/mdi/thumb-down")),
  "mdi:poll": defineAsyncComponent(() => import("~icons/mdi/poll")),
  "mdi:wallet": defineAsyncComponent(() => import("~icons/mdi/wallet")),

  // Files & Content
  "mdi:file": defineAsyncComponent(() => import("~icons/mdi/file")),
  "mdi:folder": defineAsyncComponent(() => import("~icons/mdi/folder")),
  "mdi:image": defineAsyncComponent(() => import("~icons/mdi/image")),
  "mdi:download": defineAsyncComponent(() => import("~icons/mdi/download")),
  "mdi:upload": defineAsyncComponent(() => import("~icons/mdi/upload")),
  "mdi:folder-open-outline": defineAsyncComponent(() => import("~icons/mdi/folder-open-outline")),
  "mdi:cloud-upload-outline": defineAsyncComponent(() => import("~icons/mdi/cloud-upload-outline")),
  "mdi:cloud-download": defineAsyncComponent(() => import("~icons/mdi/cloud-download")),
  "mdi:file-multiple-outline": defineAsyncComponent(() => import("~icons/mdi/file-multiple-outline")),
  "mdi:file-image": defineAsyncComponent(() => import("~icons/mdi/file-image")),
  "mdi:file-document-outline": defineAsyncComponent(() => import("~icons/mdi/file-document-outline")),
  "mdi:file-document": defineAsyncComponent(() => import("~icons/mdi/file-document")),
  "mdi:file-pdf-box": defineAsyncComponent(() => import("~icons/mdi/file-pdf-box")),
  "mdi:file-word-box": defineAsyncComponent(() => import("~icons/mdi/file-word-box")),
  "mdi:file-excel-box": defineAsyncComponent(() => import("~icons/mdi/file-excel-box")),
  "mdi:file-powerpoint-box": defineAsyncComponent(() => import("~icons/mdi/file-powerpoint-box")),
  "mdi:file-delimited": defineAsyncComponent(() => import("~icons/mdi/file-delimited")),
  "mdi:folder-zip": defineAsyncComponent(() => import("~icons/mdi/folder-zip")),
  "mdi:check-circle-outline": defineAsyncComponent(() => import("~icons/mdi/check-circle-outline")),
  "mdi:filter-outline": defineAsyncComponent(() => import("~icons/mdi/filter-outline")),
  "mdi:refresh": defineAsyncComponent(() => import("~icons/mdi/refresh")),
  "mdi:file-excel": defineAsyncComponent(() => import("~icons/mdi/file-excel")),

  // Theme
  "mdi:moon": defineAsyncComponent(() => import("~icons/mdi/weather-night")),
  "mdi:sun": defineAsyncComponent(() => import("~icons/mdi/white-balance-sunny")),
  "mdi:document": defineAsyncComponent(() => import("~icons/mdi/document")),

  // Other
  "mdi:heart": defineAsyncComponent(() => import("~icons/mdi/heart")),
  "mdi:star": defineAsyncComponent(() => import("~icons/mdi/star")),
  "mdi:calendar": defineAsyncComponent(() => import("~icons/mdi/calendar")),
  "mdi:clock": defineAsyncComponent(() => import("~icons/mdi/clock")),
  "mdi:clock-outline": defineAsyncComponent(() => import("~icons/mdi/clock-outline")),
  "mdi:link": defineAsyncComponent(() => import("~icons/mdi/link")),
  "mdi:eye": defineAsyncComponent(() => import("~icons/mdi/eye")),
  "mdi:eye-off": defineAsyncComponent(() => import("~icons/mdi/eye-off")),
  "mdi:close-circle": defineAsyncComponent(() => import("~icons/mdi/close-circle")),
  "mdi:palette": defineAsyncComponent(() => import("~icons/mdi/palette")),
  "mdi:gesture-tap": defineAsyncComponent(() => import("~icons/mdi/gesture-tap")),
  "mdi:alert-circle": defineAsyncComponent(() => import("~icons/mdi/alert-circle")),
  "mdi:lock": defineAsyncComponent(() => import("~icons/mdi/lock")),
  "mdi:trash": defineAsyncComponent(() => import("~icons/mdi/trash")),
  "mdi:list": defineAsyncComponent(() => import("~icons/mdi/view-list")),
  "mdi:content-save": defineAsyncComponent(() => import("~icons/mdi/content-save")),
  "mdi:pencil": defineAsyncComponent(() => import("~icons/mdi/pencil")),
  "mdi:puzzle": defineAsyncComponent(() => import("~icons/mdi/puzzle")),
  "mdi:vuejs": defineAsyncComponent(() => import("~icons/mdi/vuejs")),
  "mdi:language-typescript": defineAsyncComponent(() => import("~icons/mdi/language-typescript")),
  "mdi:tailwind": defineAsyncComponent(() => import("~icons/mdi/tailwind")),
  "mdi:lightning-bolt": defineAsyncComponent(() => import("~icons/mdi/lightning-bolt")),
  "mdi:check-decagram": defineAsyncComponent(() => import("~icons/mdi/check-decagram")),
  "mdi:routes": defineAsyncComponent(() => import("~icons/mdi/routes")),
  "mdi:book-open-variant": defineAsyncComponent(() => import("~icons/mdi/book-open-variant")),
  "mdi:dots-horizontal": defineAsyncComponent(() => import("~icons/mdi/dots-horizontal")),
  "mdi:view-column": defineAsyncComponent(() => import("~icons/mdi/view-column")),
  "mdi:drag-vertical": defineAsyncComponent(() => import("~icons/mdi/drag-vertical")),
  "mdi:information-outline": defineAsyncComponent(() => import("~icons/mdi/information-outline")),
  "mdi:pin-outline": defineAsyncComponent(() => import("~icons/mdi/pin-outline")),
  "mdi:pin": defineAsyncComponent(() => import("~icons/mdi/pin")),
  // ==================== Heroicons ====================

  "heroicons:home-solid": defineAsyncComponent(() => import("~icons/heroicons/home-solid")),
  "heroicons:user-solid": defineAsyncComponent(() => import("~icons/heroicons/user-solid")),
  "heroicons:cog-solid": defineAsyncComponent(() => import("~icons/heroicons/cog-6-tooth-solid")),
  "heroicons:x-mark-solid": defineAsyncComponent(() => import("~icons/heroicons/x-mark-solid")),
  "heroicons:bell-solid": defineAsyncComponent(() => import("~icons/heroicons/bell-solid")),
  "heroicons:envelope-solid": defineAsyncComponent(() => import("~icons/heroicons/envelope-solid")),

  // ==================== Lucide Icons ====================

  // KPI & Finance - Lucide
  "lucide:dollar-sign": defineAsyncComponent(() => import("~icons/lucide/dollar-sign")),
  "lucide:trending-down": defineAsyncComponent(() => import("~icons/lucide/trending-down")),
  "lucide:shopping-cart": defineAsyncComponent(() => import("~icons/lucide/shopping-cart")),
  "lucide:activity": defineAsyncComponent(() => import("~icons/lucide/activity")),
  "lucide:percent": defineAsyncComponent(() => import("~icons/lucide/percent")),
  "lucide:megaphone": defineAsyncComponent(() => import("~icons/lucide/megaphone")),
  "lucide:wallet": defineAsyncComponent(() => import("~icons/lucide/wallet")),
  "lucide:trending-up": defineAsyncComponent(() => import("~icons/lucide/trending-up")),
  "lucide:arrow-up": defineAsyncComponent(() => import("~icons/lucide/arrow-up")),
  "lucide:arrow-down": defineAsyncComponent(() => import("~icons/lucide/arrow-down")),
  "lucide:thumbs-up": defineAsyncComponent(() => import("~icons/lucide/thumbs-up")),
  "lucide:thumbs-down": defineAsyncComponent(() => import("~icons/lucide/thumbs-down")),

  // Info & Status - Lucide
  "lucide:info": defineAsyncComponent(() => import("~icons/lucide/info")),
  "lucide:circle-help": defineAsyncComponent(() => import("~icons/lucide/circle-help")),
  "lucide:alert-triangle": defineAsyncComponent(() => import("~icons/lucide/alert-triangle")),
  "lucide:x-circle": defineAsyncComponent(() => import("~icons/lucide/x-circle")),
  "lucide:check": defineAsyncComponent(() => import("~icons/lucide/check")),
  "lucide:loader": defineAsyncComponent(() => import("~icons/lucide/loader-2")),

  // Charts & Data - Lucide
  "lucide:bar-chart-3": defineAsyncComponent(() => import("~icons/lucide/bar-chart-3")),

  // Design & UI - Lucide
  "lucide:palette": defineAsyncComponent(() => import("~icons/lucide/palette")),
  "lucide:star": defineAsyncComponent(() => import("~icons/lucide/star")),
  "lucide:arrow-up-down": defineAsyncComponent(() => import("~icons/lucide/arrow-up-down")),

  // ==================== Lucide ====================

  "lucide:home": defineAsyncComponent(() => import("~icons/lucide/home")),
  "lucide:user": defineAsyncComponent(() => import("~icons/lucide/user")),
  "lucide:settings": defineAsyncComponent(() => import("~icons/lucide/settings")),
  "lucide:menu": defineAsyncComponent(() => import("~icons/lucide/menu")),
  "lucide:search": defineAsyncComponent(() => import("~icons/lucide/search")),
  "lucide:heart": defineAsyncComponent(() => import("~icons/lucide/heart")),
  "lucide:moon": defineAsyncComponent(() => import("~icons/lucide/moon")),
  "lucide:sun": defineAsyncComponent(() => import("~icons/lucide/sun")),
  "lucide:zap": defineAsyncComponent(() => import("~icons/lucide/zap")),
  "lucide:database": defineAsyncComponent(() => import("~icons/lucide/database")),
  "lucide:gauge": defineAsyncComponent(() => import("~icons/lucide/gauge")),
  "lucide:rocket": defineAsyncComponent(() => import("~icons/lucide/rocket")),
  "lucide:move-horizontal": defineAsyncComponent(() => import("~icons/lucide/move-horizontal")),
  "lucide:table-2": defineAsyncComponent(() => import("~icons/lucide/table-2")),
  "lucide:layout-grid": defineAsyncComponent(() => import("~icons/lucide/layout-grid")),
  "lucide:calculator": defineAsyncComponent(() => import("~icons/lucide/calculator")),
  "lucide:text-cursor-input": defineAsyncComponent(() => import("~icons/lucide/text-cursor-input")),
  "lucide:maximize": defineAsyncComponent(() => import("~icons/lucide/maximize")),
  "lucide:wrench": defineAsyncComponent(() => import("~icons/lucide/wrench")),
  "lucide:refresh-cw": defineAsyncComponent(() => import("~icons/lucide/refresh-cw")),
  "lucide:download": defineAsyncComponent(() => import("~icons/lucide/download")),
  "lucide:sliders": defineAsyncComponent(() => import("~icons/lucide/sliders")),
  "lucide:server": defineAsyncComponent(() => import("~icons/lucide/server")),
  "lucide:monitor": defineAsyncComponent(() => import("~icons/lucide/monitor")),
  "lucide:layers": defineAsyncComponent(() => import("~icons/lucide/layers")),
  "lucide:list": defineAsyncComponent(() => import("~icons/lucide/list")),
  "lucide:book-open": defineAsyncComponent(() => import("~icons/lucide/book-open")),
  "lucide:wand-2": defineAsyncComponent(() => import("~icons/lucide/wand-2")),
  "lucide:hash": defineAsyncComponent(() => import("~icons/lucide/hash")),
  "lucide:calendar": defineAsyncComponent(() => import("~icons/lucide/calendar")),
  "lucide:chevron-left": defineAsyncComponent(() => import("~icons/lucide/chevron-left")),
  "lucide:chevron-right": defineAsyncComponent(() => import("~icons/lucide/chevron-right")),
  "lucide:chevron-up": defineAsyncComponent(() => import("~icons/lucide/chevron-up")),
  "lucide:chevron-down": defineAsyncComponent(() => import("~icons/lucide/chevron-down")),
  "lucide:hard-drive": defineAsyncComponent(() => import("~icons/lucide/hard-drive")),
  "lucide:code-2": defineAsyncComponent(() => import("~icons/lucide/code-2")),
  "lucide:chevrons-down-up": defineAsyncComponent(() => import("~icons/lucide/chevrons-down-up")),
  "lucide:folder-tree": defineAsyncComponent(() => import("~icons/lucide/folder-tree")),
  "lucide:hand": defineAsyncComponent(() => import("~icons/lucide/hand")),
  "lucide:code": defineAsyncComponent(() => import("~icons/lucide/code")),
  "lucide:pin": defineAsyncComponent(() => import("~icons/lucide/pin")),
  "lucide:align-left": defineAsyncComponent(() => import("~icons/lucide/align-left")),
  "lucide:align-right": defineAsyncComponent(() => import("~icons/lucide/align-right")),
  "lucide:group": defineAsyncComponent(() => import("~icons/lucide/group")),
  "lucide:align-center": defineAsyncComponent(() => import("~icons/lucide/align-center")),
  "lucide:columns": defineAsyncComponent(() => import("~icons/lucide/columns")),
  "lucide:check-square": defineAsyncComponent(() => import("~icons/lucide/check-square")),
  "lucide:mouse-pointer-click": defineAsyncComponent(() => import("~icons/lucide/mouse-pointer-click")),
  "lucide:git-branch": defineAsyncComponent(() => import("~icons/lucide/git-branch")),
  "lucide:split": defineAsyncComponent(() => import("~icons/lucide/split")),
  "lucide:list-checks": defineAsyncComponent(() => import("~icons/lucide/list-checks")),
  "lucide:layout-panel-top": defineAsyncComponent(() => import("~icons/lucide/layout-panel-top")),
  "lucide:boxes": defineAsyncComponent(() => import("~icons/lucide/boxes")),
  "lucide:settings-2": defineAsyncComponent(() => import("~icons/lucide/settings-2")),
  "lucide:smartphone": defineAsyncComponent(() => import("~icons/lucide/smartphone")),
  "lucide:message-square": defineAsyncComponent(() => import("~icons/lucide/message-square")),
  "lucide:bell": defineAsyncComponent(() => import("~icons/lucide/bell")),
  "lucide:shapes": defineAsyncComponent(() => import("~icons/lucide/shapes")),
  "lucide:package": defineAsyncComponent(() => import("~icons/lucide/package")),
  "lucide:shield-check": defineAsyncComponent(() => import("~icons/lucide/shield-check")),
  "lucide:tag": defineAsyncComponent(() => import("~icons/lucide/tag")),
  "lucide:layout": defineAsyncComponent(() => import("~icons/lucide/layout")),
  "lucide:square": defineAsyncComponent(() => import("~icons/lucide/square")),
  "lucide:clipboard-list": defineAsyncComponent(() => import("~icons/lucide/clipboard-list")),
  "lucide:clock": defineAsyncComponent(() => import("~icons/lucide/clock")),
  "lucide:upload": defineAsyncComponent(() => import("~icons/lucide/upload")),
  "lucide:folder": defineAsyncComponent(() => import("~icons/lucide/folder")),
  "lucide:shield": defineAsyncComponent(() => import("~icons/lucide/folder")),
  "lucide:slash": defineAsyncComponent(() => import("~icons/lucide/slash")),
  "lucide:edit-3": defineAsyncComponent(() => import("~icons/lucide/edit-3")),
  "lucide:toggle-left": defineAsyncComponent(() => import("~icons/lucide/toggle-left")),
  "lucide:type": defineAsyncComponent(() => import("~icons/lucide/type")),
  "lucide:watch": defineAsyncComponent(() => import("~icons/lucide/watch")),
  "lucide:minus-square": defineAsyncComponent(() => import("~icons/lucide/minus-square")),
  "lucide:link": defineAsyncComponent(() => import("~icons/lucide/link")),
  "lucide:sparkles": defineAsyncComponent(() => import("~icons/lucide/sparkles")),
  "lucide:chevrons-down": defineAsyncComponent(() => import("~icons/lucide/chevrons-down")),
  "lucide:accessibility": defineAsyncComponent(() => import("~icons/lucide/accessibility")),
  "lucide:trash": defineAsyncComponent(() => import("~icons/lucide/trash")),
  "lucide:columns-3": defineAsyncComponent(() => import("~icons/lucide/columns-3")),
  "lucide:panel-left": defineAsyncComponent(() => import("~icons/lucide/panel-left")),
  "lucide:sliders-horizontal": defineAsyncComponent(() => import("~icons/lucide/sliders-horizontal")),
  "lucide:save": defineAsyncComponent(() => import("~icons/lucide/save")),
  "lucide:minimize-2": defineAsyncComponent(() => import("~icons/lucide/minimize-2")),
  "lucide:message-circle": defineAsyncComponent(() => import("~icons/lucide/message-circle")),
  "lucide:move": defineAsyncComponent(() => import("~icons/lucide/move")),
  "lucide:layout-template": defineAsyncComponent(() => import("~icons/lucide/layout-template")),
  "lucide:more-vertical": defineAsyncComponent(() => import("~icons/lucide/more-vertical")),
  "lucide:check-circle": defineAsyncComponent(() => import("~icons/lucide/check-circle")),
  "lucide:edit": defineAsyncComponent(() => import("~icons/lucide/edit")),
  "lucide:copy": defineAsyncComponent(() => import("~icons/lucide/copy")),
  "lucide:archive": defineAsyncComponent(() => import("~icons/lucide/archive")),
  "lucide:trash-2": defineAsyncComponent(() => import("~icons/lucide/trash-2")),
  "lucide:share-2": defineAsyncComponent(() => import("~icons/lucide/share")),
  "lucide:log-out": defineAsyncComponent(() => import("~icons/lucide/log-out")),
  "lucide:panel-right": defineAsyncComponent(() => import("~icons/lucide/panel-right")),
  "lucide:filter": defineAsyncComponent(() => import("~icons/lucide/filter")),
  "lucide:bookmark": defineAsyncComponent(() => import("~icons/lucide/bookmark")),
  "mdi:view-list": defineAsyncComponent(() => import("~icons/mdi/view-list")),
  "mdi:compass": defineAsyncComponent(() => import("~icons/mdi/compass")),
  "lucide:maximize-2": defineAsyncComponent(() => import("~icons/lucide/maximize-2")),
  "lucide:help-circle": defineAsyncComponent(() => import("~icons/lucide/help-circle")),
  "lucide:lightbulb": defineAsyncComponent(() => import("~icons/lucide/lightbulb")),
  "lucide:loader-2": defineAsyncComponent(() => import("~icons/lucide/loader-2")),
  "lucide:scroll": defineAsyncComponent(() => import("~icons/lucide/scroll")),
  "lucide:monitor-play": defineAsyncComponent(() => import("~icons/lucide/monitor-play")),
  "lucide:download-cloud": defineAsyncComponent(() => import("~icons/lucide/download-cloud")),
  "lucide:indent": defineAsyncComponent(() => import("~icons/lucide/indent")),
  "lucide:move-vertical": defineAsyncComponent(() => import("~icons/lucide/move-vertical")),
  "lucide:rows": defineAsyncComponent(() => import("~icons/lucide/rows")),
  "lucide:paintbrush": defineAsyncComponent(() => import("~icons/lucide/paintbrush")),
  "lucide:eye": defineAsyncComponent(() => import("~icons/lucide/eye")),
  "lucide:mouse-pointer": defineAsyncComponent(() => import("~icons/lucide/mouse-pointer")),
  "lucide:shopping-bag": defineAsyncComponent(() => import("~icons/lucide/shopping-bag")),
  "lucide:receipt": defineAsyncComponent(() => import("~icons/lucide/receipt")),
  "lucide:users": defineAsyncComponent(() => import("~icons/lucide/users")),
  "lucide:tags": defineAsyncComponent(() => import("~icons/lucide/tags")),
  "lucide:layout-dashboard": defineAsyncComponent(() => import("~icons/lucide/layout-dashboard")),
  "lucide:laptop": defineAsyncComponent(() => import("~icons/lucide/laptop")),
  "lucide:shirt": defineAsyncComponent(() => import("~icons/lucide/shirt")),
  "lucide:minus": defineAsyncComponent(() => import("~icons/lucide/minus")),
  "lucide:gift": defineAsyncComponent(() => import("~icons/lucide/gift")),
  "lucide:circle": defineAsyncComponent(() => import("~icons/lucide/circle")),
  "lucide:crown": defineAsyncComponent(() => import("~icons/lucide/crown")),
  "lucide:bar-chart": defineAsyncComponent(() => import("~icons/lucide/bar-chart")),
  "lucide:smile": defineAsyncComponent(() => import("~icons/lucide/smile")),
  "lucide:badge-check": defineAsyncComponent(() => import("~icons/lucide/badge-check")),
  "lucide:x": defineAsyncComponent(() => import("~icons/lucide/x")),
  "lucide:shield-alert": defineAsyncComponent(() => import("~icons/lucide/shield-alert")),
  "lucide:droplet": defineAsyncComponent(() => import("~icons/lucide/droplet")),
  "lucide:alert-circle": defineAsyncComponent(() => import("~icons/lucide/alert-circle")),
  "lucide:flask-conical": defineAsyncComponent(() => import("~icons/lucide/flask-conical")),
  "lucide:badge": defineAsyncComponent(() => import("~icons/lucide/badge")),
  "lucide:square-slash": defineAsyncComponent(() => import("~icons/lucide/square-slash")),
  "lucide:circle-dot": defineAsyncComponent(() => import("~icons/lucide/circle-dot")),
  "lucide:loading": defineAsyncComponent(() => import("~icons/lucide/loader-2")),
  "lucide:mail": defineAsyncComponent(() => import("~icons/lucide/mail")),
  "lucide:lock": defineAsyncComponent(() => import("~icons/lucide/lock")),
  "lucide:keyboard": defineAsyncComponent(() => import("~icons/lucide/keyboard")),
  "lucide:git-compare": defineAsyncComponent(() => import("~icons/lucide/git-compare")),
  "lucide:rotate-ccw": defineAsyncComponent(() => import("~icons/lucide/rotate-ccw")),
  "lucide:bar-chart-2": defineAsyncComponent(() => import("~icons/lucide/bar-chart-2")),
  "lucide:credit-card": defineAsyncComponent(() => import("~icons/lucide/credit-card")),
  "lucide:package-search": defineAsyncComponent(() => import("~icons/lucide/package-search")),
  "lucide:calendar-range": defineAsyncComponent(() => import("~icons/lucide/calendar-range")),
  "lucide:calendar-days": defineAsyncComponent(() => import("~icons/lucide/calendar-days")),
  "lucide:plus": defineAsyncComponent(() => import("~icons/lucide/plus")),
  "lucide:building-2": defineAsyncComponent(() => import("~icons/lucide/building-2")),
  "lucide:bookmark-x": defineAsyncComponent(() => import("~icons/lucide/bookmark-x")),
  "lucide:store": defineAsyncComponent(() => import("~icons/lucide/store")),
  "lucide:file-spreadsheet": defineAsyncComponent(() => import("~icons/lucide/file-spreadsheet")),
  "lucide:file-text": defineAsyncComponent(() => import("~icons/lucide/file-text")),
  "lucide:circle-dollar-sign": defineAsyncComponent(() => import("~icons/lucide/circle-dollar-sign")),
  "lucide:package-x": defineAsyncComponent(() => import("~icons/lucide/package-x")),
  "lucide:message-square-warning": defineAsyncComponent(() => import("~icons/lucide/message-square-warning")),
  "lucide:pie-chart": defineAsyncComponent(() => import("~icons/lucide/pie-chart")),
  "lucide:flag": defineAsyncComponent(() => import("~icons/lucide/flag")),
  "lucide:target": defineAsyncComponent(() => import("~icons/lucide/target")),
  "lucide:coins": defineAsyncComponent(() => import("~icons/lucide/coins")),
  "lucide:scatter-chart": defineAsyncComponent(() => import("~icons/lucide/scatter-chart")),
  "lucide:chart-line": defineAsyncComponent(() => import("~icons/lucide/chart-line")),
  "lucide:line-chart": defineAsyncComponent(() => import("~icons/lucide/line-chart")),
  "lucide:file-down": defineAsyncComponent(() => import("~icons/lucide/file-down")),
  "lucide:text": defineAsyncComponent(() => import("~icons/lucide/text")),
  "lucide:grid-3x3": defineAsyncComponent(() => import("~icons/lucide/grid-3x3")),
  "lucide:award": defineAsyncComponent(() => import("~icons/lucide/award")),
  "lucide:inbox": defineAsyncComponent(() => import("~icons/lucide/inbox")),
  "lucide:truck": defineAsyncComponent(() => import("~icons/lucide/truck")),
  "lucide:package-check": defineAsyncComponent(() => import("~icons/lucide/package-check")),
  "lucide:warehouse": defineAsyncComponent(() => import("~icons/lucide/warehouse")),
  "lucide:message-square-text": defineAsyncComponent(() => import("~icons/lucide/message-square-text")),
  "lucide:barcode": defineAsyncComponent(() => import("~icons/lucide/barcode")),
  "lucide:chart-column": defineAsyncComponent(() => import("~icons/lucide/chart-column")),
  "lucide:arrow-down-circle": defineAsyncComponent(() => import("~icons/lucide/arrow-down-circle")),
  "lucide:arrow-up-circle": defineAsyncComponent(() => import("~icons/lucide/arrow-up-circle")),
  "lucide:arrow-left-right": defineAsyncComponent(() => import("~icons/lucide/arrow-left-right")),
  "lucide:file-minus": defineAsyncComponent(() => import("~icons/lucide/file-minus")),
  "lucide:list-x": defineAsyncComponent(() => import("~icons/lucide/list-x")),
  "lucide:search-x": defineAsyncComponent(() => import("~icons/lucide/search-x")),
  "lucide:calendar-clock": defineAsyncComponent(() => import("~icons/lucide/calendar-clock")),
  "lucide:box": defineAsyncComponent(() => import("~icons/lucide/box")),
  "lucide:package-open": defineAsyncComponent(() => import("~icons/lucide/package-open")),
  "lucide:minus-circle": defineAsyncComponent(() => import("~icons/lucide/minus-circle")),
  "lucide:user-x": defineAsyncComponent(() => import("~icons/lucide/user-x")),
  "lucide:cloud-upload": defineAsyncComponent(() => import("~icons/lucide/cloud-upload")),
  "lucide:user-plus": defineAsyncComponent(() => import("~icons/lucide/user-plus")),
  "lucide:refresh-ccw": defineAsyncComponent(() => import("~icons/lucide/refresh-ccw")),
  "lucide:divide": defineAsyncComponent(() => import("~icons/lucide/divide")),
  "lucide:text-search": defineAsyncComponent(() => import("~icons/lucide/text-search")),
  "lucide:mouse-pointer-2": defineAsyncComponent(() => import("~icons/lucide/mouse-pointer-2")),
  "lucide:globe": defineAsyncComponent(() => import("~icons/lucide/globe")),
  "lucide:sigma": defineAsyncComponent(() => import("~icons/lucide/sigma")),
  "lucide:file": defineAsyncComponent(() => import("~icons/lucide/file")),
  "lucide:chart-pie": defineAsyncComponent(() => import("~icons/lucide/chart-pie")),
  "lucide:package-2": defineAsyncComponent(() => import("~icons/lucide/package-2")),
  "lucide:banknote": defineAsyncComponent(() => import("~icons/lucide/banknote")),
  "lucide:timer": defineAsyncComponent(() => import("~icons/lucide/timer")),
  "lucide:package-plus": defineAsyncComponent(() => import("~icons/lucide/package-plus")),
  "lucide:circle-check": defineAsyncComponent(() => import("~icons/lucide/circle-check")),
  "lucide:circle-x": defineAsyncComponent(() => import("~icons/lucide/circle-x")),
  "lucide:arrow-left": defineAsyncComponent(() => import("~icons/lucide/arrow-left")),
  "lucide:key-round": defineAsyncComponent(() => import("~icons/lucide/key-round")),
  "lucide:shield-off": defineAsyncComponent(() => import("~icons/lucide/shield-off")),
  "lucide:send": defineAsyncComponent(() => import("~icons/lucide/send")),
  "lucide:container": defineAsyncComponent(() => import("~icons/lucide/container")),
};

/**
 * Get list of all available icons
 */
export const getAvailableIcons = (): string[] => {
  return Object.keys(iconMap).sort();
};

/**
 * Check if icon exists in the map
 */
export const hasIcon = (iconName: string): boolean => {
  return iconName in iconMap;
};
